/**
 * License Insights counting rules (spec docs/superpowers/specs/2026-10-25-license-insights-design.md, section 6).
 *
 * Pure and dependency free. The collector (lambda/license-rules.mjs, a byte-for-byte copy pinned by
 * lambda/tests/license-catalog-drift.test.mjs) counts every (user, product) pair with classifyPair for each
 * inactivity and grace option, so the portal shows exact counts for whatever the admin picks without a new
 * collection. The portal uses the same function to place the people it shows the customer's admin.
 *
 * Counting rule (no double counting): a (user, product) pair counts in exactly one finding, in this precedence:
 * LIC-001 disabled, then LIC-002 inactive, then LIC-003 duplicate, then LIC-008 product not used (a product whose use
 * Microsoft reports per person, license-catalog.mjs USAGE_REPORTED_PRODUCTS: none of its services used in the period),
 * then LIC-007 no Microsoft 365 activity at all (the products whose use onGuard cannot check). A disabled account still
 * inside the admin's grace period counts nowhere ('grace'). Free products never reach this function.
 */

export const INACTIVITY_OPTIONS = Object.freeze([30, 60, 90, 180]);
export const DEFAULT_INACTIVITY_DAYS = 90;
export const GRACE_OPTIONS = Object.freeze([0, 30, 60]);
export const DEFAULT_GRACE_DAYS = 0;

/** The per-user findings, in precedence order. */
export const PAIR_FINDINGS = Object.freeze(['LIC-001', 'LIC-002', 'LIC-003', 'LIC-008', 'LIC-007']);

/** Assignment states that hold a seat (Microsoft licenseAssignmentState.state). */
export const ACTIVE_STATES = Object.freeze(['Active', 'ActiveWithError']);
/** Assignment states with an error (group licensing). */
export const ERROR_STATES = Object.freeze(['Error', 'ActiveWithError']);

/** The key of one (inactivity, grace) combination in the collector's counts. */
export function variantKey(inactivityDays, graceDays) {
  return `${inactivityDays}:${graceDays}`;
}

/** Every variant the collector counts. */
export const VARIANT_KEYS = Object.freeze(INACTIVITY_OPTIONS.flatMap((i) => GRACE_OPTIONS.map((g) => variantKey(i, g))));

/** The settings as stored, made safe: an unknown value falls back to the default. */
export function normalizeSettings(settings) {
  const i = Number(settings?.inactivityDays);
  const g = Number(settings?.disabledGraceDays);
  return {
    inactivityDays: INACTIVITY_OPTIONS.includes(i) ? i : DEFAULT_INACTIVITY_DAYS,
    disabledGraceDays: GRACE_OPTIONS.includes(g) ? g : DEFAULT_GRACE_DAYS,
  };
}

const older = (days, n) => days === null || (typeof days === 'number' && days >= n);
const oldEnough = (createdDays, n) => createdDays == null || createdDays >= n;

/**
 * Which finding one (user, product) pair counts in, or 'grace', or null.
 *
 * @param {object} person
 * @param {boolean} person.disabled - accountEnabled === false
 * @param {number|null} [person.disabledDays] - days since onGuard knows the account was disabled; null when unknown
 *   (an account already disabled before onGuard saw it: never inside a grace period)
 * @param {number|null} [person.createdDays] - account age in days; null when unknown
 * @param {number|null|undefined} [person.signInDays] - days since the latest sign-in; null = never signed in;
 *   undefined = not evaluated (no Microsoft Entra ID P1 or no AuditLog.Read.All)
 * @param {number|null|undefined} [person.useDays] - days since the latest Microsoft 365 activity; null = no activity;
 *   undefined = not evaluated (usage reports off)
 * @param {{ redundantWith?: string|null, useDays?: number|null }} license - redundantWith: the product that already
 *   provides every plan; useDays: days since the person last used one of this product's services (null = never in the
 *   report period; absent = not checked: usage reports off, or Microsoft reports no per-person use of the product)
 * @param {{ inactivityDays: number, disabledGraceDays: number }} settings
 * @returns {'LIC-001'|'LIC-002'|'LIC-003'|'LIC-008'|'LIC-007'|'grace'|null}
 */
export function classifyPair(person, license, settings) {
  const n = settings.inactivityDays;
  const grace = settings.disabledGraceDays;
  if (person.disabled === true) {
    if (grace > 0 && typeof person.disabledDays === 'number' && person.disabledDays < grace) return 'grace';
    return 'LIC-001';
  }
  if (person.signInDays !== undefined && oldEnough(person.createdDays, n) && older(person.signInDays, n)) return 'LIC-002';
  if (license.redundantWith) return 'LIC-003';
  if (license.useDays !== undefined && oldEnough(person.createdDays, n) && older(license.useDays, n)) return 'LIC-008';
  if (person.useDays !== undefined && oldEnough(person.createdDays, n) && older(person.useDays, n)) return 'LIC-007';
  return null;
}
